import { useState } from "react";
import { apiFetch } from "../services/api";

/**
 * Custom hook for managing invites
 */
const useInvites = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if a pending invite exists for a specific bar and artist
   */
  const checkPendingInvite = async (barId, artistId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/invites/pending/${barId}/${artistId}`);
      return response;
    } catch (err) {
      // Don't set error for 403 - it just means user doesn't have permission, which is fine
      if (err.message && !err.message.includes("403")) {
        setError(err.message);
      }
      // Return a default response instead of throwing for permission errors
      return {
        success: false,
        hasPending: false,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send batch invites to an artist for multiple bars
   * @param {Array<number>} barIds - Array of bar IDs to invite to
   * @param {number} artistId - Artist ID (will be converted to user_id on backend)
   * @param {string} message - Optional message
   * @param {string} inviteType - Type of invite (default: "artist")
   */
  const sendBatchInvite = async (barIds, artistId, message, inviteType = "artist") => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch("/invites/batch", {
        method: "POST",
        body: JSON.stringify({
          barIds,
          artistId, // Backend will look up user_id from artist_id
          inviteType,
          message,
        }),
      });
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get all pending invites for an artist
   */
  const getArtistInvites = async (artistId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/invites/artist/${artistId}`);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Respond to an invite (accept or decline)
   */
  const respondToInvite = async (inviteId, action) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/invites/${inviteId}/respond`, {
        method: "PATCH",
        body: JSON.stringify({ action }),
      });
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get invite details by ID
   */
  const getInviteById = async (inviteId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/invites/${inviteId}`);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get all received invites for current user
   */
  const getReceivedInvites = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch("/invites/received");
      
      // Ensure we return a proper response even if API doesn't
      if (!response) {
        return {
          success: false,
          error: "No response from server",
          invites: [],
        };
      }
      
      return response;
    } catch (err) {
      console.error("Error in getReceivedInvites:", err);
      setError(err.message);
      // Return error response instead of throwing
      return {
        success: false,
        error: err.message || "Failed to fetch invites",
        invites: [],
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    checkPendingInvite,
    sendBatchInvite,
    getArtistInvites,
    respondToInvite,
    getInviteById,
    getReceivedInvites,
  };
};

export default useInvites;

